import {createContext, use, useMemo, useSyncExternalStore} from 'react'
import {changesetReducer} from '@microsoft/agent-host-protocol'
import type {ChangesetAction, ChangesetFile, ChangesetState} from '@microsoft/agent-host-protocol'
import type {EstablishedAhpSession, EstablishedAhpSessionView} from '@github-ui/ahp-session-client/contracts'
import type {TaskHistorySession} from '../utils/replay-task-history-events'
import {findSavedChangesetUri, findSessionChangesetUri} from '../utils/find-session-changeset-uri'
import {
  EstablishedAhpSessionContext,
  EstablishedAhpSessionSnapshotContext,
} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpSessionContext'

export const SavedTaskChangesetContext = createContext<TaskHistorySession | undefined>(undefined)

const EMPTY_FILES: readonly ChangesetFile[] = []

export interface UseTaskChangesetResult {
  /** True once the session's changeset catalogue advertises a whole-session changeset entry. */
  readonly available: boolean
  /** True while `available` but no snapshot or error has arrived yet. */
  readonly loading: boolean
  /** Computation lifecycle from the most recent snapshot; `undefined` until the first one arrives. */
  readonly status: ChangesetState['status'] | undefined
  readonly files: readonly ChangesetFile[]
  readonly error: string | undefined
  readonly persisted?: boolean
}

interface ChangesetEntrySnapshot {
  readonly state: ChangesetState | undefined
  readonly error: string | undefined
}

interface ChangesetEntry {
  snapshot: ChangesetEntrySnapshot
  unsubscribe: () => void
  readonly owners: Set<EstablishedAhpSessionView>
  refCount: number
  /** True once `ensureSubscribed` has been called for this entry, ever. */
  hasStartedSubscribing: boolean
  /** True while a real, live `observeChangeset` registration is active for this entry. Distinct
   * from `!snapshot.error` — a failed-to-apply action also records an error but stays subscribed. */
  subscribed: boolean
  readonly activeViews: Map<EstablishedAhpSessionView, number>
  /** Backoff state for retrying a failed subscribe attempt; see {@link scheduleRetry}. */
  retryTimer: ReturnType<typeof setTimeout> | undefined
  retryAttempt: number
  /** Armed when `refCount` drops to zero; see {@link scheduleEviction}. */
  idleTimer: ReturnType<typeof setTimeout> | undefined
  readonly listeners: Set<() => void>
}

const IDLE_SNAPSHOT: ChangesetEntrySnapshot = {state: undefined, error: undefined}
const NO_ENTRY_SNAPSHOT: ChangesetEntrySnapshot = {state: undefined, error: undefined}
const subscribeToNoEntry = () => () => undefined
const getNoEntrySnapshot = () => NO_ENTRY_SNAPSHOT

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

/** A generation owns its changeset registration. The URI can be absolute and shared by unrelated
 * sessions; transient views of the same session and relay generation still share one subscription. */
const entriesByKey = new Map<string, ChangesetEntry>()

function entryKey(identity: EstablishedAhpSession['identity'], generation: number, uri: string): string {
  return JSON.stringify([
    identity.taskId ?? null,
    identity.environmentId,
    identity.sessionId,
    identity.channel ?? null,
    generation,
    uri,
  ])
}

function disposeEntry(key: string, entry: ChangesetEntry): void {
  if (entry.idleTimer !== undefined) clearTimeout(entry.idleTimer)
  if (entry.retryTimer !== undefined) clearTimeout(entry.retryTimer)
  entry.idleTimer = undefined
  entry.retryTimer = undefined
  if ([...entry.owners].some(owner => owner.isCurrent())) entry.unsubscribe()
  entriesByKey.delete(key)
}

function getEntry(key: string): ChangesetEntry {
  let entry = entriesByKey.get(key)
  if (!entry) {
    // An old relay may already have disposed its runtime. Only unsubscribe registrations
    // whose generation still owns the runtime; otherwise discard their cached state.
    for (const [oldKey, oldEntry] of entriesByKey) {
      if (oldKey !== key && oldEntry.refCount === 0 && ![...oldEntry.owners].some(owner => owner.isCurrent())) {
        disposeEntry(oldKey, oldEntry)
      }
    }
    entry = {
      snapshot: IDLE_SNAPSHOT,
      unsubscribe: () => {},
      owners: new Set(),
      refCount: 0,
      hasStartedSubscribing: false,
      subscribed: false,
      activeViews: new Map(),
      retryTimer: undefined,
      retryAttempt: 0,
      idleTimer: undefined,
      listeners: new Set(),
    }
    entriesByKey.set(key, entry)
  }
  return entry
}

/** How long an idle (zero-listener) entry stays cached before its real subscription is torn down
 * and it's evicted from `entriesByKey` — long enough to survive normal navigating away and back to
 * the same session, short enough to actually bound memory for a long-lived tab that visits many
 * sessions. Mirrors the `disposeTimer` grace-period pattern in `relay-registry.ts`. */
const IDLE_EVICT_GRACE_MS = 10 * 60 * 1000

/** Tears down an idle entry only if its generation still owns a live relay registration. */
function scheduleEviction(key: string, entry: ChangesetEntry): void {
  if (entry.idleTimer !== undefined) return
  entry.idleTimer = setTimeout(() => {
    entry.idleTimer = undefined
    if (entriesByKey.get(key) !== entry || entry.refCount > 0) return
    disposeEntry(key, entry)
  }, IDLE_EVICT_GRACE_MS)
}

function cancelEviction(entry: ChangesetEntry): void {
  if (entry.idleTimer === undefined) return
  clearTimeout(entry.idleTimer)
  entry.idleTimer = undefined
}

/** Bounded backoff for a failed subscribe attempt — rides out a brief startup delay without
 * retrying forever in the background. */
const MAX_RETRY_ATTEMPTS = 5
const RETRY_BASE_DELAY_MS = 250

function scheduleRetry(uri: string, entry: ChangesetEntry): void {
  if (entry.retryTimer !== undefined) return
  if (entry.listeners.size === 0) return
  if (entry.retryAttempt >= MAX_RETRY_ATTEMPTS) return
  const delay = RETRY_BASE_DELAY_MS * 2 ** entry.retryAttempt
  entry.retryAttempt++
  entry.retryTimer = setTimeout(() => {
    entry.retryTimer = undefined
    if (entry.listeners.size === 0) return
    ensureSubscribed(uri, entry)
  }, delay)
}

/** Starts the real relay subscription for `entry`, retrying with backoff on failure. */
function ensureSubscribed(uri: string, entry: ChangesetEntry): void {
  const view = [...entry.activeViews.keys()].at(-1)
  const observe = view?.observeChangeset
  if (!observe) {
    entry.snapshot = {state: undefined, error: 'This session does not support live changesets.'}
    return
  }

  const setSnapshot = (next: ChangesetEntrySnapshot) => {
    entry.snapshot = next
    for (const listener of entry.listeners) listener()
  }

  // `observe()` can error synchronously before returning its unsubscribe function — this flag
  // tells the error handler whether it's safe to call `entry.unsubscribe` immediately or must wait.
  let subscribedSynchronously = false
  let erroredBeforeReturn = false

  const handleError = (message: string) => {
    setSnapshot({state: entry.snapshot.state, error: message})
    entry.subscribed = false
    if (subscribedSynchronously) {
      entry.unsubscribe()
      entry.unsubscribe = () => {}
    } else {
      erroredBeforeReturn = true
    }
    scheduleRetry(uri, entry)
  }

  try {
    entry.unsubscribe = observe(uri, {
      onSnapshot: state => {
        entry.retryAttempt = 0
        setSnapshot({state, error: undefined})
      },
      onAction: envelope => {
        // An action before any snapshot has nothing to apply to, so it's safely dropped, not errored.
        // A failure here doesn't touch `entry.subscribed` — unlike a real `handleError` disconnect.
        const current = entry.snapshot.state
        if (!current) return
        try {
          setSnapshot({state: changesetReducer(current, envelope.action as ChangesetAction), error: undefined})
        } catch (err) {
          setSnapshot({state: current, error: errorMessage(err, 'Failed to apply a changeset update.')})
        }
      },
      onError: handleError,
    })
    subscribedSynchronously = true
    if (erroredBeforeReturn) {
      entry.unsubscribe()
      entry.unsubscribe = () => {}
    } else {
      entry.subscribed = true
    }
  } catch (err) {
    entry.snapshot = {state: undefined, error: errorMessage(err, 'Failed to subscribe to the changeset.')}
    entry.subscribed = false
    scheduleRetry(uri, entry)
  }
}

function subscribeEntry(view: EstablishedAhpSessionView, uri: string, key: string, entry: ChangesetEntry) {
  return (listener: () => void) => {
    entry.listeners.add(listener)
    entry.owners.add(view)
    entry.refCount++
    cancelEviction(entry)
    entry.activeViews.set(view, (entry.activeViews.get(view) ?? 0) + 1)
    if (!entry.hasStartedSubscribing) {
      entry.hasStartedSubscribing = true
      ensureSubscribed(uri, entry)
    } else if (!entry.subscribed && entry.retryTimer === undefined) {
      // Not live and no retry pending — a fresh listener is worth one more subscribe attempt.
      ensureSubscribed(uri, entry)
    }
    return () => {
      entry.listeners.delete(listener)
      entry.refCount--
      const count = entry.activeViews.get(view)
      if (count === 1) entry.activeViews.delete(view)
      else if (count !== undefined) entry.activeViews.set(view, count - 1)
      // Doesn't unsubscribe immediately when `refCount` reaches zero — see `scheduleEviction`.
      if (entry.refCount === 0) scheduleEviction(key, entry)
    }
  }
}

/** Surfaces the live status/files of the session's whole-session changeset. `enabled: false`
 * keeps this from ever creating the underlying relay subscription. */
export function useTaskChangeset(enabled = true): UseTaskChangesetResult {
  const session = use(EstablishedAhpSessionContext)
  const snapshot = use(EstablishedAhpSessionSnapshotContext)
  const saved = use(SavedTaskChangesetContext)
  const matchingSaved = !session || session.identity.sessionId === saved?.sessionId ? saved : undefined
  const savedUri = enabled ? findSavedChangesetUri(matchingSaved) : undefined
  const uri = enabled
    ? findSessionChangesetUri(snapshot?.sessionState?.changesets, session?.identity.sessionId)
    : undefined
  const view = session?.view
  const identity = session?.identity

  // Memoized on the view and cache identity so an unrelated re-render doesn't tear down the subscription.
  const {subscribe, getSnapshot} = useMemo(() => {
    if (!view || !uri || !identity) return {subscribe: subscribeToNoEntry, getSnapshot: getNoEntrySnapshot}
    const key = entryKey(identity, view.generation, uri)
    const entry = getEntry(key)
    return {subscribe: subscribeEntry(view, uri, key, entry), getSnapshot: () => entry.snapshot}
  }, [view, uri, identity])

  const storeSnapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const selectedUri = snapshot?.sessionState !== undefined ? uri : savedUri
  const persisted = selectedUri ? matchingSaved?.bundle.changesetStates?.[selectedUri] : undefined
  const catalogueError =
    enabled && snapshot?.sessionState === undefined ? matchingSaved?.bundle.changesetCatalogError : undefined
  const usePersisted = !storeSnapshot.state && (!!persisted || !!catalogueError)

  return useMemo(
    () => ({
      available: !!selectedUri,
      loading: !!selectedUri && !storeSnapshot.state && !persisted && !storeSnapshot.error && !catalogueError,
      status: storeSnapshot.state?.status ?? persisted?.status,
      files: storeSnapshot.state?.files ?? persisted?.files ?? EMPTY_FILES,
      error: storeSnapshot.error ?? (usePersisted ? (catalogueError ?? persisted?.error) : undefined),
      persisted: usePersisted,
    }),
    [selectedUri, storeSnapshot, persisted, usePersisted, catalogueError],
  )
}

const subscribeToNoSession = () => () => undefined

/** Reactively reports whether a session (passed directly, not from context) advertises a
 * `'session'`-kind changeset — for callers deciding whether to mount a subtree from outside it. */
export function useHasSessionChangeset(session: EstablishedAhpSession | undefined): boolean {
  const subscribe = session ? session.view.subscribe : subscribeToNoSession
  const getSnapshot = () =>
    !!findSessionChangesetUri(session?.view.getSnapshot().sessionState?.changesets, session?.identity.sessionId)
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
